import { useAccount } from "wagmi";

import { useRx } from "@/contexts/rx";
import { useFeatureFlags } from "@/hooks/feature";
import { usePoolCoeffs, usePoolData, usePoolParams } from "@/hooks/position";
import { usePushSubject } from "@/hooks/rx";
import { useBalanceNormalize } from "@/hooks/token";
import { useApply } from "@/hooks/util";

export const useChainData = (pool?: `0x${string}`) => {
  const account = useAccount();
  const { error$ } = useRx();
  const flags = useFeatureFlags(pool);
  const coeffs = usePoolCoeffs(pool);

  const {
    base,
    basePrice,
    quote,
    position,
    getPositionParams,
    uniswapAddress,
    unwrap,
    refetch,
    baseAddress,
    quoteAddress,
  } = usePoolData(pool);

  const params = useApply(getPositionParams, coeffs);
  const { maxLeverage } = usePoolParams(pool);

  const baseBalance = useBalanceNormalize({
    address: account.address,
    token: unwrap !== "base" ? base?.address : undefined,
  });

  usePushSubject(error$, baseBalance?.error);

  const quoteBalance = useBalanceNormalize({
    address: account.address,
    token: unwrap !== "quote" ? quote?.address : undefined,
  });

  usePushSubject(error$, quoteBalance?.error);

  return {
    /** @deprecated flags are needed for testing and migration scenarios only */
    flags,
    base,
    quote,
    baseAddress,
    baseBalance: baseBalance?.data,
    baseCollateralCoeff: coeffs?.baseCollateralCoeff,
    baseDebtCoeff: coeffs?.baseDebtCoeff,
    basePrice,
    quoteAddress,
    quoteBalance: quoteBalance?.data,
    quoteCollateralCoeff: coeffs?.quoteCollateralCoeff,
    quoteDebtCoeff: coeffs?.quoteDebtCoeff,
    realBaseAmount: params?.realBaseAmount.dp(base?.decimals ?? 18, 2),
    realQuoteAmount: params?.realQuoteAmount.dp(quote?.decimals ?? 18, 2),
    type: position?.type,
    uniswapAddress,
    unwrap,
    maxLeverage,
    refetch,
  };
};

export type ChainData = ReturnType<typeof useChainData>;
